import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/encaissements';
import { fmtDateHeure } from './EncaissementModal';

// Configuration Adyen (super-admin) : un merchant account PAR AGENCE, et les
// terminaux de chaque agence (POIID = modèle-série, lisible sur le terminal
// ou dans Customer Area ▸ Point of sale ▸ Terminals).
function EncaissementsConfig() {
  const [cfg, setCfg] = useState(null);
  const [erreur, setErreur] = useState('');
  const [compte, setCompte] = useState({ concession_id: '', merchant_account: '', store: '' });
  const [terminal, setTerminal] = useState({ id: null, concession_id: '', poiid: '', libelle: '' });
  const [webhooks, setWebhooks] = useState(null);
  const [busy, setBusy] = useState(false);

  const charger = useCallback(async () => {
    try { const { data } = await api.config(); setCfg(data); }
    catch (err) { setErreur(err.response?.data?.error || err.message); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const run = async (fn) => {
    setErreur(''); setBusy(true);
    try { await fn(); await charger(); } catch (err) { setErreur(err.response?.data?.error || err.message); } finally { setBusy(false); }
  };

  if (!cfg) return <div className="table-container" style={{ padding: 20 }}>{erreur || 'Chargement…'}</div>;
  const sansCompte = cfg.concessions.filter((c) => !cfg.comptes.some((a) => a.concession_id === c.id));

  return (
    <div>
      <div className="enc-bandeau status-info" style={{ marginBottom: 16 }}>
        Environnement Adyen : <span className={`enc-env ${cfg.env === 'live' ? 'status-error' : 'status-warning'}`}>{cfg.env.toUpperCase()}</span>
        {' · '}clé API {cfg.adyen_configure ? '✅ présente' : '❌ absente (ADYEN_API_KEY)'}
        {' · '}webhook HMAC {cfg.hmac_configure ? '✅ configuré' : '❌ absent (ADYEN_HMAC_KEY) — aucun paiement ne sera confirmé par webhook'}
        {cfg.faux_terminal && ' · 🧪 faux terminal actif'}
        <div style={{ marginTop: 6, fontSize: 12 }}>Ces réglages viennent du fichier .env du serveur. Le webhook standard Adyen doit pointer sur <code>/api/webhooks/adyen</code> (niveau company, un seul pour toutes les agences).</div>
      </div>

      <section className="enc-config-section">
        <h3>🏦 Merchant accounts — un par agence</h3>
        <p>Le merchant account d'une agence détermine le compte Adyen crédité pour ses TPE et ses liens. Une agence qui porte deux sociétés (ex. 006 + 010) garde un seul compte : la ventilation par société se fait sur la référence poussée dans Adyen.</p>
        <div className="enc-config-form">
          <div><label>Agence</label>
            <select value={compte.concession_id} onChange={(e) => setCompte((c) => ({ ...c, concession_id: e.target.value }))}>
              <option value="">— Choisir —</option>
              {cfg.concessions.map((c) => <option key={c.id} value={c.id}>{c.nom}{cfg.comptes.some((a) => a.concession_id === c.id) ? ' (remplacer)' : ''}</option>)}
            </select></div>
          <div><label>Merchant account</label><input value={compte.merchant_account} onChange={(e) => setCompte((c) => ({ ...c, merchant_account: e.target.value }))} placeholder="SellensToulouse" style={{ width: 240 }} /></div>
          <div><label>Store (facultatif)</label><input value={compte.store} onChange={(e) => setCompte((c) => ({ ...c, store: e.target.value }))} style={{ width: 140 }} /></div>
          <button className="btn-primary" disabled={busy || !compte.concession_id || !compte.merchant_account.trim()} onClick={() => run(async () => { await api.saveCompte(compte); setCompte({ concession_id: '', merchant_account: '', store: '' }); })}>Enregistrer</button>
        </div>
        <div className="table-container">
          <table>
            <thead><tr><th>Agence</th><th>Merchant account</th><th>Store</th><th>Actif</th><th></th></tr></thead>
            <tbody>
              {cfg.comptes.map((a) => (
                <tr key={a.id}>
                  <td>{a.concession_nom}</td><td className="enc-ref">{a.merchant_account}</td><td>{a.store || '—'}</td><td>{a.actif ? '✅' : '—'}</td>
                  <td><div className="action-buttons">
                    <button className="btn-icon" title="Modifier" onClick={() => setCompte({ concession_id: String(a.concession_id), merchant_account: a.merchant_account, store: a.store || '' })}>✏️</button>
                    <button className="btn-icon delete" title="Retirer" onClick={() => { if (window.confirm(`Retirer le merchant account de ${a.concession_nom} ? Ses vendeurs ne pourront plus encaisser.`)) run(() => api.deleteCompte(a.id)); }}>🗑️</button>
                  </div></td>
                </tr>
              ))}
              {!cfg.comptes.length && <tr><td colSpan={5} style={{ color: 'var(--text-muted)' }}>Aucun merchant account déclaré : aucune agence ne peut encaisser.</td></tr>}
            </tbody>
          </table>
        </div>
        {sansCompte.length > 0 && <small className="enc-hint">Sans compte : {sansCompte.map((c) => c.nom).join(', ')}.</small>}
      </section>

      <section className="enc-config-section">
        <h3>🖥️ Terminaux</h3>
        <p>Le POIID est l'identifiant du terminal chez Adyen, au format <code>modèle-numéro de série</code> (ex. V400m-324688179). Un terminal n'encaisse que pour son agence.</p>
        <div className="enc-config-form">
          <div><label>Agence</label>
            <select value={terminal.concession_id} onChange={(e) => setTerminal((t) => ({ ...t, concession_id: e.target.value }))}>
              <option value="">— Choisir —</option>
              {cfg.concessions.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select></div>
          <div><label>POIID</label><input value={terminal.poiid} onChange={(e) => setTerminal((t) => ({ ...t, poiid: e.target.value.trim() }))} placeholder="V400m-324688179" style={{ width: 200 }} /></div>
          <div><label>Libellé</label><input value={terminal.libelle} onChange={(e) => setTerminal((t) => ({ ...t, libelle: e.target.value }))} placeholder="Comptoir VN" style={{ width: 180 }} /></div>
          <button className="btn-primary" disabled={busy || !terminal.concession_id || !terminal.poiid} onClick={() => run(async () => { await api.saveTerminal(terminal); setTerminal({ id: null, concession_id: '', poiid: '', libelle: '' }); })}>{terminal.id ? 'Mettre à jour' : 'Ajouter'}</button>
          {terminal.id && <button className="btn-secondary" onClick={() => setTerminal({ id: null, concession_id: '', poiid: '', libelle: '' })}>Annuler</button>}
        </div>
        <div className="table-container">
          <table>
            <thead><tr><th>Agence</th><th>Libellé</th><th>POIID</th><th>Dernier usage</th><th>Actif</th><th></th></tr></thead>
            <tbody>
              {cfg.terminaux.map((t) => (
                <tr key={t.id}>
                  <td>{t.concession_nom}</td><td>{t.libelle}</td><td className="enc-ref">{t.poiid}</td><td>{fmtDateHeure(t.dernier_usage_le)}</td><td>{t.actif ? '✅' : '—'}</td>
                  <td><div className="action-buttons">
                    <button className="btn-icon" title="Modifier" onClick={() => setTerminal({ id: t.id, concession_id: String(t.concession_id), poiid: t.poiid, libelle: t.libelle })}>✏️</button>
                    {t.actif
                      ? <button className="btn-icon delete" title="Retirer / désactiver" onClick={() => { if (window.confirm(`Retirer le terminal ${t.libelle} ?`)) run(() => api.deleteTerminal(t.id)); }}>🗑️</button>
                      : <button className="btn-icon" title="Réactiver" onClick={() => run(() => api.saveTerminal({ id: t.id, concession_id: t.concession_id, poiid: t.poiid, libelle: t.libelle, actif: true }))}>✅</button>}
                  </div></td>
                </tr>
              ))}
              {!cfg.terminaux.length && <tr><td colSpan={6} style={{ color: 'var(--text-muted)' }}>Aucun terminal : seuls les liens de paiement sont possibles.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="enc-config-section">
        <h3>📡 Diagnostic</h3>
        <div className="enc-config-form">
          <button className="btn-secondary" disabled={busy} onClick={() => run(async () => { const { data } = await api.webhooks(); setWebhooks(data.webhooks || []); })}>Derniers webhooks reçus</button>
          <button className="btn-secondary" disabled={busy} onClick={() => run(async () => { const { data } = await api.poller(); window.alert(`Poller : ${JSON.stringify(data)}`); })}>Lancer le poller maintenant</button>
        </div>
        {webhooks && (
          <div className="table-container">
            <table>
              <thead><tr><th>Reçu</th><th>Événement</th><th>Succès</th><th>pspReference</th><th>merchantReference</th><th>Traité</th><th>Note</th></tr></thead>
              <tbody>
                {webhooks.map((w) => (
                  <tr key={w.id}><td>{fmtDateHeure(w.recu_le)}</td><td>{w.event_code}</td><td>{w.success}</td><td className="enc-ref">{w.psp_reference}</td><td className="enc-ref">{w.merchant_reference}</td><td>{w.traite ? '✅' : '⏳'}</td><td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{w.erreur || ''}</td></tr>
                ))}
                {!webhooks.length && <tr><td colSpan={7} style={{ color: 'var(--text-muted)' }}>Aucun webhook reçu.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {erreur && <div className="status-error enc-bandeau">{erreur}</div>}
    </div>
  );
}

export default EncaissementsConfig;
